import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  feedback?: {
    voteValue: number;
    feedbackText?: string;
    showFeedbackInput?: boolean;
  };
}

interface ChatbotState {
  messages: ChatMessage[];
  conversationSessionId?: string;
  userHasSentMessage: boolean;
  isModalOpen: boolean;
  sessionExpiresAt?: number; // Timestamp when session expires
  isSessionExpired: boolean; // Flag to track if session has expired
}

const initialState: ChatbotState = {
  messages: [],
  conversationSessionId: undefined,
  userHasSentMessage: false,
  isModalOpen: false,
  sessionExpiresAt: undefined,
  isSessionExpired: false,
};

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    updateMessage: (state, action: PayloadAction<{ id: string; updates: Partial<ChatMessage> }>) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message) {
        Object.assign(message, action.payload.updates);
      }
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    setConversationSessionId: (state, action: PayloadAction<string | undefined>) => {
      state.conversationSessionId = action.payload;
    },
    setUserHasSentMessage: (state, action: PayloadAction<boolean>) => {
      state.userHasSentMessage = action.payload;
    },
    setIsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    clearChat: (state) => {
      state.messages = [];
      state.userHasSentMessage = false;
    },
    setSessionExpiration: (state, action: PayloadAction<{ expiresIn: number | null }>) => {
      if (action.payload.expiresIn && action.payload.expiresIn > 0) {
        // expiresIn is in seconds, convert to milliseconds and add to current time
        state.sessionExpiresAt = Date.now() + action.payload.expiresIn * 1000;
        state.isSessionExpired = false;
      }
    },
    markSessionExpired: (state) => {
      state.isSessionExpired = true;
      state.sessionExpiresAt = undefined;
    },
    addFeedback: (
      state,
      action: PayloadAction<{
        messageId: string;
        voteValue: number;
        feedbackText?: string;
        showFeedbackInput: boolean;
      }>
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.messageId);
      if (message) {
        message.feedback = {
          voteValue: action.payload.voteValue,
          feedbackText: action.payload.feedbackText,
          showFeedbackInput: action.payload.showFeedbackInput,
        };
      }
    },
    restoreFromStorage: (state, action: PayloadAction<ChatbotState>) => {
      return action.payload;
    },
  },
});

export const {
  addMessage,
  updateMessage,
  setMessages,
  setConversationSessionId,
  setUserHasSentMessage,
  setIsModalOpen,
  clearChat,
  addFeedback,
  restoreFromStorage,
  setSessionExpiration,
  markSessionExpired,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;
